import React, { memo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import PaneBody from 'components/Common/Pane/PaneBody'
import AnimateHeight from 'react-animate-height'
import Clickable from 'components/Common/Clickable'
import Heading from 'components/Luxkit/Typography/Heading'
import BodyText from 'components/Luxkit/Typography/BodyText'
import MarkdownRender from 'components/Luxkit/MarkdownRender'
import Group from 'components/utils/Group'
import Caption from 'components/Luxkit/Typography/Caption'
import PaletteProvider from 'contexts/PaletteProvider'
import cn from 'clsx'
import LineAngleRightIcon from 'components/Luxkit/Icons/line/LineAngleRightIcon'

const AccordionSection = styled.div`
  margin-top: ${rem(16)};

  &:nth-child(2) {
    margin-top:${rem(28)};
  }

  &:last-child {
    > * {
      border-bottom-color: transparent;
    }
  }
`
const AccordionHeader = styled(Clickable)`
  display: flex;
  width: 100%;
  padding: ${rem(20)} 0;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid ${props => props.theme.palette.neutral.seven};
  background-color: ${props => props.theme.palette.neutral.seven};
  border-radius: ${props => props.theme.borderRadius.medium};
  border-bottom:unset;
  padding: ${rem(12)};

  &.open {
    border-bottom-color: transparent;
  }

  &.disable-click {
    pointer-events: none;
  }

  &.selected .day-label {
    background-color: ${props => props.theme.palette.neutral.six};
  }
`

const ArrowContainer = styled.div`
  display: flex;
  justify-content: end;

  transform: rotate(90deg);
  transition: transform 0.2s ease-out;

  &.open {
    transform: rotate(-90deg);
  }
`

const DescriptionWrapper = styled.div`
  padding: ${rem(16)} 0 0 0;
`

const PaneBodyStyled = styled(PaneBody)`
  padding: 0 ${rem(24)} 0 ${rem(18)};
`

const Section = styled.div`
  margin: ${rem(16)} 0 ${rem(16)} 0;
`

const LeftWrapper = styled.div`
  display: flex;
  align-items: center;
`

const DayLabel = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  background-color: ${props => props.theme.palette.neutral.eight};
  padding: ${rem(4)} ${rem(8)};
`

const BodyTextHeader = styled.div`
  grid-column: 2;
  grid-row: 1;
  display: flex;
  align-items: center;

  ${mediaQueryUp.tablet} {
    grid-column: 2;
    grid-row: 2;
  }
`

interface Props {
  itineraryItem: App.CruiseItineraryItem;
  open: boolean;
  isSelected: boolean;
  toggleAccordion: (key: string, item: App.CruiseItineraryItem) => void;
}

function CruiseAccordion(props: Props) {
  const { itineraryItem, open, toggleAccordion, isSelected } = props

  const atSea = !itineraryItem.title && !itineraryItem.port

  const originalTitle = itineraryItem.title || null

  const emptyContent = atSea || itineraryItem.description.trim() === ''

  const hasDeparture = itineraryItem.startTime !== '--'
  const hasArrival = itineraryItem.endTime !== '--'
  const onlyDeparture = hasDeparture && !hasArrival
  const onlyArrival = !hasDeparture && hasArrival

  return (
    <AccordionSection>
      <PaletteProvider type={isSelected ? 'inverse' : 'default'}>
        <AccordionHeader className={cn({ open, selected: isSelected })} onClick={() => toggleAccordion(`${itineraryItem.startDay}`, itineraryItem)}>
          <LeftWrapper>
            <Group direction="horizontal" gap={12} verticalAlign="center">
              <DayLabel className="day-label">
                <Group direction="vertical">
                  <Caption variant="medium" colour="neutral-two">Day</Caption>
                  <BodyText variant="medium" colour="neutral-one" weight="bold">{itineraryItem.startDay}</BodyText>
                </Group>
              </DayLabel>
              <div>
                <Heading variant="heading5">
                  {originalTitle || (atSea ? 'At Sea' : itineraryItem.port?.name)}
                </Heading>

                {!atSea && <BodyTextHeader>
                  <BodyText variant="small" colour="neutral-one">
                    {hasDeparture && itineraryItem.startTime}
                    {!onlyDeparture && !onlyArrival && ' - '}
                    {hasArrival && itineraryItem.endTime}
                  </BodyText>
                </BodyTextHeader>}
              </div>
            </Group>
          </LeftWrapper>

          {!emptyContent && <ArrowContainer className={cn({ open })}>
            <LineAngleRightIcon colour="neutral-one" />
          </ArrowContainer>}
        </AccordionHeader>
      </PaletteProvider>

      {!atSea && <AnimateHeight height={open ? 'auto' : 0}>
        <DescriptionWrapper>
          <PaneBodyStyled size="lg-x">
            <Heading variant="heading5">{itineraryItem.title}</Heading>
            <Section>
              <MarkdownRender content={itineraryItem.description} fontSize="medium" />
            </Section>
          </PaneBodyStyled>
        </DescriptionWrapper>
      </AnimateHeight>}
    </AccordionSection>
  )
}

export default memo(CruiseAccordion)
